import type { ReactNode } from 'react';
import { ImageIcon } from 'lucide-react';
import {
  cookTimeMinutes,
  entityImagesDetailed,
  entityLocalized,
  entityName,
  entityNumber,
  entityText,
  formatMoney,
  measurementLabel,
  entityString,
  entityBoolean,
  productStatusLabel,
  requestCategoryName,
  type ChangeRequest,
  type ModerationEntity,
  type Translate,
} from '@/entities/moderation';
import { useVendorName } from '../../../model/use-vendor-name';
import { formatFieldValue } from '../../../lib/field-labels';
import { PreviewableImage } from '../image-preview';
import { ModifiersSection } from '../modifiers-section';
import { InfoRow, ReviewSection, ReviewSectionTitle } from '../review-modal-shell';
import { LocalizedRows } from './localized-rows';

/**
 * A dish as one column (Figma 616:17247 body): hero row (main photo, name,
 * vendor / category / price / cook time / unit / availability), the photo
 * strip, the name and description in three locales, and modifiers.
 */
export function ProductSnapshot({
  entity,
  request,
  t,
  verdictInfo,
}: {
  entity: ModerationEntity;
  request: ChangeRequest;
  t: Translate;
  verdictInfo?: ReactNode;
}) {
  const vendorName = useVendorName(request.merchant_id);
  const name = entityName(entity);
  const image = entityImagesDetailed(entity)[0]?.url ?? '';
  const extraImages = entityImagesDetailed(entity).slice(1, 8);
  const price = entityNumber(entity, 'effective_price') ?? entityNumber(entity, 'price');
  const cookTime = cookTimeMinutes(entity);
  const category = entityText(entity, 'product_category_name', '');
  // `weight_grams` was renamed `measure_amount`; older queued snapshots still carry it.
  const weight =
    entityNumber(entity, 'measure_amount') ?? entityNumber(entity, 'weight_grams');
  const measurement = entityString(entity, 'measurement_type');
  const status = entityString(entity, 'status');

  return (
    <>
      <div className="flex flex-col items-start gap-4 sm:flex-row">
        <div className="w-full shrink-0 sm:w-[220px]">
          {image !== '' ? (
            <PreviewableImage
              src={image}
              className="aspect-square w-full rounded-[16px] bg-muted object-cover"
            />
          ) : (
            <EmptyPhoto label={t('detail.noPhoto', 'Нет фотографии')} />
          )}
          {image !== '' && extraImages.length > 0 && (
            <div className="mt-2 grid grid-cols-5 gap-2">
              {extraImages.map((extra, index) => (
                <PreviewableImage
                  key={`${extra.url}-${index}`}
                  src={extra.url}
                  className="aspect-square w-full rounded-lg bg-muted object-cover"
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex w-full min-w-0 flex-1 flex-col gap-3">
          {verdictInfo && (
            <ReviewSectionTitle>
              {t('create.infoSection', 'Основная информация')}
            </ReviewSectionTitle>
          )}
          <dl className="flex flex-col gap-2.5">
            <InfoRow label={t('productCreate.vendor', 'Вендор')} value={vendorName} />
            <InfoRow
              label={t('productCreate.category', 'Категория')}
              value={category !== '' ? category : requestCategoryName(request)}
            />
            <InfoRow
              label={t('detail.price', 'Цена')}
              value={price === undefined ? '—' : formatMoney(price)}
              numeric
            />
            <InfoRow
              label={t('detail.cookTime', 'Время приготовления')}
              value={
                cookTime === undefined
                  ? '—'
                  : t('review.minutes', '{{n}} мин.').replace('{{n}}', String(cookTime))
              }
              numeric
            />
            {(weight !== undefined || measurement !== '') && (
              <InfoRow
                label={t('review.unit', 'Единица / вес')}
                value={[
                  measurement !== '' ? measurementLabel(measurement) : '',
                  weight !== undefined
                    ? `${weight} ${measurement !== '' ? measurementLabel(measurement) : 'g'}`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
                numeric
              />
            )}
            {status !== '' && (
              <InfoRow
                label={t('review.availability', 'Доступность')}
                value={productStatusLabel(status, t)}
              />
            )}
            {verdictInfo}
          </dl>
        </div>
      </div>

      <ReviewSection title={t('productCreate.nameSection', 'Название товара')}>
        <LocalizedRows value={name} t={t} />
      </ReviewSection>

      <ReviewSection title={t('productCreate.descriptionSection', 'Описание товара')}>
        <LocalizedRows value={entityLocalized(entity, 'description')} t={t} stacked />
      </ReviewSection>

      <ReviewSection title={t('productCreate.fiscalSection', 'Данные для фискализации')}>
        <dl className="flex flex-col gap-2.5">
          <InfoRow label="ИКПУ" value={entityString(entity, 'ikpu_code') || '—'} />
          <InfoRow
            label={t('fields.measurement_type', 'Отображение единицы в приложении')}
            value={formatFieldValue('measurement_type', measurement, t)}
          />
          <InfoRow
            label={t('fields.measure_amount', 'Количество')}
            value={weight ?? '—'}
            numeric
          />
          <InfoRow
            label={t('fields.unit_code', 'Единица измерения')}
            value={formatFieldValue('unit_code', entityString(entity, 'unit_code'), t)}
          />
          <InfoRow
            label={t('fields.vat_percent', 'НДС, %')}
            value={entityNumber(entity, 'vat_percent') ?? '—'}
            numeric
          />
          <InfoRow
            label={t('review.marking', 'Маркировка')}
            value={
              entityBoolean(entity, 'has_marked_code')
                ? t('detail.yes', 'Да')
                : t('detail.no', 'Нет')
            }
          />
          {entityString(entity, 'marked_code') && (
            <InfoRow
              label={t('review.markedCode', 'Код маркировки')}
              value={entityString(entity, 'marked_code')}
            />
          )}
        </dl>
      </ReviewSection>

      <ModifiersSection own={entity} other={null} highlight={false} t={t} layout="grid" />
    </>
  );
}

function EmptyPhoto({ label }: { label: string }) {
  return (
    <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-[16px] bg-muted text-muted-foreground">
      <ImageIcon aria-hidden className="size-8" />
      <p className="text-sm">{label}</p>
    </div>
  );
}
